/**
 * product-tracking.js
 * Fenge Section Level Analytics - Product Page Tracking Module
 *
 * 负责追踪 (PDP 产品页):
 *   - variant_select : 用户选择颜色/尺寸/款式
 *   - media_switch   : 用户点击产品图缩略图切换主图
 *   - video_play     : 产品页视频开始播放
 *
 * @version 1.0.0
 */

(function () {
  'use strict';

  // ─── 工具函数 ────────────────────────────────────────────────────────────────

  function publishEvent(eventName, eventData) {
    if (!window.Shopify || !window.Shopify.analytics || typeof window.Shopify.analytics.publish !== 'function') {
      return;
    }
    try {
      window.Shopify.analytics.publish(eventName, eventData);
    } catch (e) {
      // 生产环境静默
    }
  }

  function getSectionId(el) {
    const wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  // ─── 变体选择追踪 ─────────────────────────────────────────────────────────────

  /**
   * 追踪变体选择事件
   * 通过监听 variant picker 的 change 事件（radio/select/swatch 按钮）
   */
  function initVariantSelectTracking() {
    // 监听 variant picker 的 change 事件（input[type=radio] / select）
    document.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!input) return;

        // 仅追踪 product form 内的 variant 选择
        const productForm = input.closest('form[id^="ProductForm-"]');
        if (!productForm) return;

        // 获取 option 名称（颜色/尺寸等），从 input name 或 closest label 获取
        let optionName = '';
        let optionValue = '';

        if (input.type === 'radio' || input.tagName === 'SELECT') {
          optionName =
            input.dataset.option ||
            input.name ||
            '';
          optionValue =
            input.tagName === 'SELECT'
              ? input.options[input.selectedIndex]?.text || input.value
              : input.value;
        }

        if (!optionName || !optionValue) return;

        // 提取 option_name 中的选项组名（Shopify variant picker 的 name 格式 is options[Color] 等）
        const optionMatch = optionName.match(/options\[([^\]]+)\]/);
        const cleanOptionName = optionMatch ? optionMatch[1] : optionName;

        const sectionWrapper = input.closest('[id^="shopify-section-"]');
        const sectionId = sectionWrapper
          ? sectionWrapper.id.replace('shopify-section-', '')
          : '';

        publishEvent('variant_select', {
          section_id: sectionId,
          option_name: cleanOptionName,
          option_value: optionValue,
          page_path: window.location.pathname,
          product_title: document
            .querySelector('h1.heading')
            ?.textContent?.trim() || '',
        });
      },
      { passive: true }
    );

    // 同时监听 swatch button 点击（部分 swatch UI 不使用原生 input change）
    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest(
          '[data-option-value], .variant-swatch, .swatch-button, [is="variant-swatch"]'
        );
        if (!btn) return;

        const productForm = btn.closest(
          'form[id^="ProductForm-"], product-info'
        );
        if (!productForm) return;

        const optionValue =
          btn.dataset.optionValue ||
          btn.dataset.value ||
          btn.textContent.trim();
        const optionName =
          btn.dataset.optionName ||
          btn.closest('[data-option]')?.dataset.option ||
          '';

        if (!optionValue) return;

        const sectionWrapper = btn.closest('[id^="shopify-section-"]');
        const sectionId = sectionWrapper
          ? sectionWrapper.id.replace('shopify-section-', '')
          : '';

        publishEvent('variant_select', {
          section_id: sectionId,
          option_name: optionName,
          option_value: optionValue,
          page_path: window.location.pathname,
          product_title: document
            .querySelector('h1.heading')
            ?.textContent?.trim() || '',
        });
      },
      { passive: true }
    );
  }

  // ─── 产品图片切换追踪 ─────────────────────────────────────────────────────────

  /**
   * 追踪产品媒体图缩略图点击（media_switch）
   * 监听 product-thumbnail 或 product-media-gallery 内的 thumbnail 点击
   */
  function initMediaSwitchTracking() {
    document.addEventListener(
      'click',
      (event) => {
        // 产品图缩略图通常在 .product__media-list 或 .product-media-gallery 内
        const thumb = event.target.closest(
          '.product__media-item, .product-thumbnail, [data-media-id], .product__thumbnail'
        );
        if (!thumb) return;

        // 确认在产品 section 内
        const sectionWrapper = thumb.closest('[id^="shopify-section-"]');
        if (!sectionWrapper) return;
        const sectionId = sectionWrapper.id.replace('shopify-section-', '');

        const mediaId =
          thumb.dataset.mediaId ||
          thumb.dataset.thumbnailId ||
          thumb.id ||
          '';
        const mediaType =
          thumb.dataset.mediaType ||
          (thumb.querySelector('video, model-viewer') ? 'video' : 'image');

        // 获取图片 URL 作为 media 标识
        const img = thumb.querySelector('img');
        const mediaUrl = img
          ? (img.src || img.dataset.src || '').split('?')[0]
          : '';

        publishEvent('media_switch', {
          section_id: sectionId,
          media_id: mediaId,
          media_type: mediaType,
          media_url: mediaUrl,
          page_path: window.location.pathname,
        });
      },
      { passive: true }
    );
  }

  // ─── 视频播放追踪 ─────────────────────────────────────────────────────────────

  /**
   * 追踪视频播放事件（产品页及各 Section 内的 video 元素）
   */
  function initVideoPlayTracking(scope) {
    const videos = (scope || document).querySelectorAll(
      'video, deferred-media'
    );

    videos.forEach((mediaEl) => {
      // 对于 deferred-media（Shopify 延迟加载 iframe/video），监听其 play 事件
      if (mediaEl.tagName === 'DEFERRED-MEDIA') {
        mediaEl.addEventListener('play', () => fireVideoPlay(mediaEl));
      } else {
        // 原生 video 元素
        mediaEl.addEventListener('play', () => fireVideoPlay(mediaEl), {
          once: false,
          passive: true,
        });
      }
    });
  }

  function fireVideoPlay(mediaEl) {
    const sectionWrapper = mediaEl.closest('[id^="shopify-section-"]');
    const sectionId = sectionWrapper
      ? sectionWrapper.id.replace('shopify-section-', '')
      : '';

    const videoEl =
      mediaEl.tagName === 'VIDEO'
        ? mediaEl
        : mediaEl.querySelector('video');

    const videoSrc = videoEl
      ? videoEl.currentSrc || videoEl.querySelector('source')?.src || ''
      : '';
    const videoDur = videoEl?.duration || 0;

    publishEvent('video_play', {
      section_id: sectionId,
      video_src: videoSrc.split('?')[0],
      video_dur: Math.round(videoDur),
      page_path: window.location.pathname,
    });
  }

  // ─── 初始化入口 ───────────────────────────────────────────────────────────────

  function init(scope) {
    if (
      document.documentElement.classList.contains('shopify-design-mode') ||
      window.Shopify?.designMode
    ) {
      return;
    }
    // 变体追踪全局只注册一次
    if (!window.__fengeProductTrackingInited) {
      window.__fengeProductTrackingInited = true;
      initVariantSelectTracking();
      initMediaSwitchTracking();
    }
    initVideoPlayTracking(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    initVideoPlayTracking(e.target);
  });
})();
